import React, { useState, useEffect, useRef } from 'react';
import { 
  ShieldCheck, 
  Activity, 
  Zap, 
  Maximize, 
  Mic, 
  Home, 
  Download, 
  Printer, 
  Search,
  Keyboard,
  Clock,
  AlertTriangle,
  FileText,
  Lock,
  KeyRound
} from 'lucide-react';

const App = () => {
  // --- State ---
  const [text, setText] = useState('');
  const [logs, setLogs] = useState([]);
  const [sessionActive, setSessionActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [finalReport, setFinalReport] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cheatingDetected, setCheatingDetected] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockError, setUnlockError] = useState(false);
  const [retakeAttempts, setRetakeAttempts] = useState(0);
  
  const [stats, setStats] = useState({
    keystrokes: 0, 
    pasteCount: 0, 
    blurCount: 0, 
    avgFlightTime: 0,
    burstCount: 0, 
    patternVariance: 0, 
    backspaces: 0, 
    deletions: 0, 
    rhythmScore: 100
  });

  // --- Styles Object ---
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#05070a',
      color: '#cbd5e1',
      padding: '2rem',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#0d1117',
      padding: '1.25rem 2rem',
      borderRadius: '1.25rem',
      border: '1px solid #30363d',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      position: 'sticky',
      top: '1rem',
      zIndex: 50
    },
    brandArea: { display: 'flex', alignItems: 'center', gap: '1.25rem' },
    iconBox: {
      backgroundColor: '#4f46e5',
      padding: '0.8rem',
      borderRadius: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 0 20px rgba(79, 70, 229, 0.3)'
    },
    title: {
      fontSize: '1.4rem',
      fontWeight: '900',
      color: '#fff',
      margin: 0,
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    statusBadge: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.7rem',
      fontWeight: '800',
      color: active ? '#4ade80' : '#94a3b8',
      textTransform: 'uppercase',
      marginTop: '0.2rem',
      letterSpacing: '0.1em'
    }),
    btnGroup: { display: 'flex', gap: '0.75rem' },
    buttonSecondary: {
      padding: '0.6rem 1.2rem',
      borderRadius: '0.75rem',
      backgroundColor: '#161b22',
      color: '#f0f6fc',
      border: '1px solid #30363d',
      fontWeight: '700',
      fontSize: '0.85rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    buttonPrimary: (active, disabled) => ({
      padding: '0.6rem 1.8rem',
      borderRadius: '0.75rem',
      backgroundColor: active ? 'rgba(239, 68, 68, 0.1)' : '#4f46e5',
      color: active ? '#ef4444' : '#fff',
      border: active ? '1px solid rgba(239, 68, 68, 0.5)' : 'none',
      fontWeight: '900',
      fontSize: '0.85rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      textTransform: 'uppercase',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s ease'
    }),
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 320px',
      gap: '2rem'
    },
    editorPane: {
      backgroundColor: '#0d1117',
      borderRadius: '1.5rem',
      border: '1px solid #30363d',
      minHeight: '650px',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
    },
    textarea: {
      flex: 1,
      width: '100%',
      padding: '3rem',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      color: '#e6edf3',
      fontSize: '1.2rem',
      lineHeight: '1.8',
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      resize: 'none',
      tabSize: 4
    },
    sidebar: { display: 'flex', flexDirection: 'column', gap: '1.5rem' },
    card: {
      backgroundColor: '#0d1117',
      padding: '1.5rem',
      borderRadius: '1.25rem',
      border: '1px solid #30363d'
    },
    logBox: {
      height: '420px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      paddingRight: '0.5rem',
      fontSize: '0.75rem'
    },
    logEntry: (type) => ({
      padding: '0.8rem',
      borderRadius: '0.75rem',
      border: '1px solid',
      backgroundColor: type === 'danger' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(22, 27, 34, 1)',
      borderColor: type === 'danger' ? 'rgba(239, 68, 68, 0.3)' : '#30363d',
      color: type === 'danger' ? '#f85149' : '#8b949e',
      lineHeight: '1.4'
    }),
    reportContainer: {
      backgroundColor: '#fff',
      color: '#000',
      padding: '4rem',
      borderRadius: '0.5rem',
      minHeight: '1000px',
      boxShadow: '0 0 50px rgba(0,0,0,0.5)',
      fontFamily: 'serif'
    },
    statBox: {
      backgroundColor: '#161b22',
      padding: '1.25rem',
      borderRadius: '1rem',
      border: '1px solid #30363d'
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      backgroundColor: 'rgba(5, 7, 10, 0.98)',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '2rem'
    },
    inputCode: {
      backgroundColor: '#161b22',
      border: '1px solid #30363d',
      borderRadius: '0.5rem',
      padding: '0.75rem 1rem',
      color: '#fff',
      textAlign: 'center',
      fontSize: '1.2rem',
      letterSpacing: '0.3em',
      width: '200px',
      marginBottom: '1rem',
      outline: 'none'
    }
  };

  // --- Refs ---
  const lastKeyTime = useRef(Date.now());
  const flightTimes = useRef([]);
  const containerRef = useRef(null);

  // --- Load cached state on mount ---
  useEffect(() => {
    const cachedCheating = sessionStorage.getItem('synthetica_cheating_detected');
    const cachedSession = localStorage.getItem('synthetica_session_active');
    const cachedText = localStorage.getItem('synthetica_text');
    const cachedStats = localStorage.getItem('synthetica_stats');
    const cachedRetakes = localStorage.getItem('synthetica_retake_attempts');
    
    if (cachedCheating === 'true') {
      setCheatingDetected(true);
    }
    if (cachedSession === 'true') {
      setSessionActive(true);
    }
    if (cachedText) {
      setText(cachedText);
    }
    if (cachedStats) {
      try {
        setStats(JSON.parse(cachedStats));
      } catch (e) {
        console.warn('Failed to parse cached stats');
      }
    }
    if (cachedRetakes) {
      setRetakeAttempts(parseInt(cachedRetakes) || 0);
    }
  }, []);

  // --- Save session state to cache ---
  useEffect(() => {
    localStorage.setItem('synthetica_session_active', sessionActive.toString());
  }, [sessionActive]);

  // --- Save text and stats during session ---
  useEffect(() => {
    if (sessionActive) {
      localStorage.setItem('synthetica_text', text);
      localStorage.setItem('synthetica_stats', JSON.stringify(stats));
    }
  }, [text, stats, sessionActive]);

  // --- Save retake attempts ---
  useEffect(() => {
    localStorage.setItem('synthetica_retake_attempts', retakeAttempts.toString());
  }, [retakeAttempts]);

  // --- Strict Fullscreen Logic ---
  useEffect(() => {
    const handleFsChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      
      if (!isFull && sessionActive) {
        // Wipe content and lock terminal
        setSessionActive(false);
        setCheatingDetected(true);
        setRetakeAttempts(prev => prev + 1);
        setText(''); // Wipe test content
        // Clear session data from cache on violation
        localStorage.removeItem('synthetica_session_active');
        localStorage.removeItem('synthetica_text');
        localStorage.removeItem('synthetica_stats');
        // Use sessionStorage for cheating detection (clears on reload)
        sessionStorage.setItem('synthetica_cheating_detected', 'true');
        addLog("CRITICAL: Fullscreen exit detected. DATA PURGED. Session terminated.", "danger");
      }
    };

    const handleVisibility = () => {
      if (document.hidden && sessionActive) {
        setStats(p => ({ ...p, blurCount: p.blurCount + 1 }));
        addLog("WARNING: Browser tab focus lost.", "danger");
      }
    };

    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [sessionActive]);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [{ timestamp: new Date().toLocaleTimeString(), message, type }, ...prev].slice(0, 50));
  };

  const handleKeyDown = (e) => {
    if (!sessionActive) return;
    const now = Date.now();
    const flightTime = now - lastKeyTime.current;

    // Accuracy Tracking
    if (e.key === 'Backspace') setStats(p => ({ ...p, backspaces: p.backspaces + 1 }));
    if (e.key === 'Delete') setStats(p => ({ ...p, deletions: p.deletions + 1 }));
    
    // Rhythm Analysis (exclude backspace and other non-typing keys)
    const isTypingKey = e.key.length === 1 || e.key === 'Enter' || e.key === 'Tab' || e.key === ' ';
    if (isTypingKey && stats.keystrokes > 0) {
      flightTimes.current.push(flightTime);
      const avg = flightTimes.current.reduce((a, b) => a + b, 0) / flightTimes.current.length;
      
      // Check for suspicious bursts (e.g. copy-paste simulation)
      const isBurst = flightTime < 40;
      
      setStats(p => ({ 
        ...p, 
        keystrokes: p.keystrokes + 1, 
        avgFlightTime: Math.round(avg),
        burstCount: isBurst ? p.burstCount + 1 : p.burstCount
      }));

      if (isBurst && stats.keystrokes % 10 === 0) {
        addLog("Suspicious typing rhythm detected (Burst Mode).", "danger");
      }
    } else if (isTypingKey) {
      setStats(p => ({ ...p, keystrokes: 1 }));
    }
    lastKeyTime.current = now;
  };

  const handleDownload = () => {
    const reportText = `
SYNTHETICA AUDIT REPORT
========================
Timestamp: ${finalReport?.timestamp}
Verdict: ${finalReport?.verdict}

TELEMETRY DATA
--------------
Total Keystrokes: ${stats.keystrokes}
Backspaces: ${stats.backspaces}
Deletions: ${stats.deletions}
Average Key Latency: ${stats.avgFlightTime}ms
Suspicious Bursts: ${stats.burstCount}
Focus Loss Events: ${stats.blurCount}

SUBMISSION CONTENT
------------------
${text}

-------------------------
CERTIFIED BY SYNTHETICA PROCTOR ENGINE
    `;
    const blob = new Blob([reportText], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Synthetica_Audit_${Date.now()}.txt`;
    link.click();
  };

  const toggleSession = async () => {
    if (!sessionActive) {
      if (!isFullscreen) return addLog("Security Error: Assessment requires Fullscreen mode.", "danger");
      setSessionActive(true);
      setCheatingDetected(false);
      setFinalReport(null);
      setStats({ keystrokes: 0, pasteCount: 0, blurCount: 0, avgFlightTime: 0, burstCount: 0, patternVariance: 100, backspaces: 0, deletions: 0, rhythmScore: 100 });
      setLogs([]);
      addLog("Session initialized. All sensors active.", "info");
    } else {
      setSessionActive(false);
      setAnalyzing(true);
      setTimeout(() => {
        // Adjusted verdict logic for leniency
        setFinalReport({ 
          verdict: stats.burstCount > 50 ? "Suspicious (Excessive Burst Rate)" : "Verified Human", 
          aiLinguisticScore: 0.08, 
          behaviorScore: 0.02, 
          timestamp: new Date().toLocaleString() 
        });
        setAnalyzing(false);
        // Clear session data from cache after generating report
        localStorage.removeItem('synthetica_session_active');
        localStorage.removeItem('synthetica_text');
        localStorage.removeItem('synthetica_stats');
      }, 2000);
    }
  };

  const startAssessment = () => {
    containerRef.current.requestFullscreen().then(() => {
      toggleSession();
    });
  };

  const handleUnlock = () => {
    if (unlockCode === '123456') {
      setCheatingDetected(false);
      setUnlockCode('');
      setUnlockError(false);
      // Clear all cached data on unlock
      sessionStorage.removeItem('synthetica_cheating_detected');
      localStorage.removeItem('synthetica_session_active');
      localStorage.removeItem('synthetica_text');
      localStorage.removeItem('synthetica_stats');
      addLog("Terminal Reset: Access granted by proctor code.", "info");
    } else {
      setUnlockError(true);
      setTimeout(() => setUnlockError(false), 1000);
    }
  };

  return (
    <div ref={containerRef} style={styles.container}>
      {/* Cheating Alert & Lockout Overlay */}
      {cheatingDetected && (
        <div style={styles.overlay}>
          <AlertTriangle size={80} color="#ef4444" style={{ marginBottom: '1.5rem' }} />
          <h2 style={{ fontSize: '3rem', fontWeight: '900', color: '#fff' }}>ASSESSMENT TERMINATED</h2>
          <p style={{ fontSize: '1.2rem', color: '#f87171', maxWidth: '600px', marginTop: '1rem', fontWeight: 'bold' }}>
            System detected a fullscreen exit violation. DATA HAS BEEN PURGED.
          </p>
          <p style={{ fontSize: '1rem', color: '#fbbf24', maxWidth: '600px', marginTop: '0.5rem', fontWeight: 'bold' }}>
            Retake Attempt #{retakeAttempts}
          </p>
          <p style={{ color: '#94a3b8', maxWidth: '500px', marginTop: '1rem', marginBottom: '2rem' }}>
            The terminal is now locked. To reset this station and retake the assessment, an authorized proctor must enter the security override passcode.
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <input 
                type="password" 
                maxLength="6"
                placeholder="••••••"
                style={{
                  ...styles.inputCode,
                  borderColor: unlockError ? '#ef4444' : '#30363d',
                  boxShadow: unlockError ? '0 0 20px rgba(239, 68, 68, 0.2)' : 'none',
                  transition: 'all 0.2s'
                }}
                value={unlockCode}
                onChange={(e) => setUnlockCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
              />
              <KeyRound size={16} color="#8b949e" style={{ position: 'absolute', right: '-30px', top: '15px' }} />
            </div>
            <button 
              style={{ ...styles.buttonPrimary(false), marginTop: '1rem', width: '200px', justifyContent: 'center' }} 
              onClick={handleUnlock}
            >
              Unlock Terminal
            </button>
          </div>
        </div>
      )}

      <header style={styles.header}>
        <div style={styles.brandArea}>
          <div style={styles.iconBox}><ShieldCheck color="white" size={24} /></div>
          <div>
            <h1 style={styles.title}>Synthetica <span style={{ color: '#818cf8' }}>Node</span></h1>
            <div style={styles.statusBadge(sessionActive)}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: sessionActive ? '#4ade80' : '#f85149', boxShadow: sessionActive ? '0 0 10px #4ade80' : 'none' }} />
              {sessionActive ? 'Live Encryption Active' : 'Waiting for Secure Handshake'}
            </div>
          </div>
        </div>
        
        <div style={styles.btnGroup}>
          
          <button 
            style={styles.buttonPrimary(sessionActive, false)} 
            onClick={sessionActive ? toggleSession : startAssessment}
          >
            {sessionActive ? <><Zap size={16} /> Terminate</> : <><Lock size={16} /> Secure & Start</>}
          </button>
        </div>
      </header>

      <div style={styles.grid}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {finalReport ? (
            <div style={styles.reportContainer} className="report-print">
              <style>{`
                @media print {
                  body * { visibility: hidden; }
                  .report-print, .report-print * { visibility: visible; }
                  .report-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; border: none; }
                  .no-print { display: none !important; }
                }
              `}</style>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #000', paddingBottom: '1.5rem', marginBottom: '3rem' }}>
                <div>
                  <h2 style={{ fontSize: '2.5rem', margin: 0 }}>SYNTHETICA AUDIT</h2>
                  <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>Biometric Verification & Linguistic Analysis</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontWeight: 'bold' }}>ID: {Math.random().toString(36).substring(7).toUpperCase()}</p>
                  <p>{finalReport.timestamp}</p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', marginBottom: '4rem' }}>
                 <div style={{ border: '1px solid #ddd', padding: '1rem' }}>
                    <p style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Integrity Verdict</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: finalReport.verdict.includes('Suspicious') ? 'red' : 'green' }}>{finalReport.verdict}</p>
                 </div>
                 <div style={{ border: '1px solid #ddd', padding: '1rem' }}>
                    <p style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', marginBottom: '0.5rem' }}>AI Likelihood</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{(finalReport.aiLinguisticScore * 100).toFixed(1)}%</p>
                 </div>
                 <div style={{ border: '1px solid #ddd', padding: '1rem' }}>
                    <p style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Behavior Score</p>
                    {/* More lenient scoring formula: reduced penalty per burst (2 points vs 5) */}
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{Math.max(0, 100 - (stats.burstCount * 2)).toFixed(0)}/100</p>
                 </div>
              </div>

              <div style={{ marginBottom: '4rem' }}>
                 <h3 style={{ fontSize: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>SUBMISSION CONTENT</h3>
                 <div style={{ whiteSpace: 'pre-wrap', fontSize: '1.1rem', lineHeight: '1.6' }}>{text || "No content provided."}</div>
              </div>

              <div className="no-print" style={{ display: 'flex', gap: '1rem', marginTop: '4rem', borderTop: '1px solid #eee', paddingTop: '2rem' }}>
                 <button style={{ ...styles.buttonSecondary, backgroundColor: '#000', color: '#fff' }} onClick={handleDownload}><Download size={16} /> Download .TXT</button>
                 <button style={{ ...styles.buttonSecondary, backgroundColor: '#000', color: '#fff' }} onClick={() => window.print()}><Printer size={16} /> Print Report</button>
                 <button style={{ ...styles.buttonSecondary }} onClick={() => setFinalReport(null)}>Discard & Reset</button>
              </div>
            </div>
          ) : (
            <div style={styles.editorPane}>
              {analyzing && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(5, 7, 10, 0.9)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}>
                  <div style={{ position: 'relative' }}>
                    <div style={{ width: '60px', height: '60px', border: '3px solid rgba(79, 70, 229, 0.1)', borderTop: '3px solid #4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    <Search size={24} color="#4f46e5" style={{ position: 'absolute', inset: 0, margin: 'auto' }} />
                  </div>
                  <p style={{ fontWeight: '900', letterSpacing: '0.2em', color: '#fff', fontSize: '0.8rem' }}>CRYPTO-ANALYSIS IN PROGRESS</p>
                </div>
              )}
              <div style={{ padding: '0.8rem 1.5rem', borderBottom: '1px solid #30363d', backgroundColor: '#161b22', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                   <span style={{ fontSize: '0.65rem', fontWeight: '900', color: '#8b949e', letterSpacing: '0.1em' }}>TERMINAL_ID: SYS-882</span>
                   <div style={{ width: '1px', height: '12px', backgroundColor: '#30363d' }} />
                   <span style={{ fontSize: '0.65rem', color: isFullscreen ? '#4ade80' : '#f85149' }}>
                     {isFullscreen ? 'FULLSCREEN_LOCKED' : 'SECURITY_BREACH: WINDOWED_MODE'}
                   </span>
                </div>
                {sessionActive && (
                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.65rem', fontWeight: '800', color: '#8b949e' }}>
                    <span>CADENCE: {stats.avgFlightTime}ms</span>
                    <span>BURSTS: {stats.burstCount}</span>
                  </div>
                )}
              </div>
              <textarea
                style={styles.textarea}
                placeholder={sessionActive ? "Begin typing your assessment content. Your typing signature is being recorded..." : "Assessment Locked. Click 'Secure & Start' to begin."}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!sessionActive || analyzing}
                spellCheck={false}
              />
            </div>
          )}
        </div>

        <aside style={styles.sidebar}>
          <div style={styles.card}>
            <h3 style={{ fontSize: '0.7rem', color: '#818cf8', fontWeight: '900', marginBottom: '1.25rem', letterSpacing: '0.1em' }}>BIO-SENSORS</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={styles.statBox}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.6rem', color: '#8b949e', fontWeight: '800' }}>RHYTHM VAR</span>
                    <span style={{ fontSize: '0.6rem', color: '#fff' }}>{stats.burstCount > 25 ? 'HIGH' : 'STABLE'}</span>
                 </div>
                 <div style={{ width: '100%', height: '4px', backgroundColor: '#0d1117', borderRadius: '2px' }}>
                    <div style={{ width: `${Math.min(100, stats.burstCount * 2)}%`, height: '100%', backgroundColor: stats.burstCount > 35 ? '#f85149' : '#818cf8', borderRadius: '2px', transition: 'width 0.3s ease' }} />
                 </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div style={styles.statBox}>
                  <span style={{ display: 'block', fontSize: '0.5rem', color: '#8b949e', fontWeight: '800', marginBottom: '0.3rem' }}>LATENCY</span>
                  <span style={{ fontSize: '1.2rem', fontWeight: '900', color: '#fff' }}>{stats.avgFlightTime}<span style={{ fontSize: '0.6rem', color: '#8b949e' }}>ms</span></span>
                </div>
                <div style={styles.statBox}>
                  <span style={{ display: 'block', fontSize: '0.5rem', color: '#8b949e', fontWeight: '800', marginBottom: '0.3rem' }}>REDACTS</span>
                  <span style={{ fontSize: '1.2rem', fontWeight: '900', color: '#fff' }}>{stats.backspaces + stats.deletions}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '0.7rem', color: '#8b949e', fontWeight: '900', letterSpacing: '0.1em' }}>SECURITY FEED</h3>
              <Activity size={14} color="#30363d" />
            </div>
            <div style={styles.logBox}>
              {logs.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#30363d', textAlign: 'center', fontSize: '0.65rem' }}>
                  NO LIVE FEED<br/>AWAITING INITIALIZATION
                </div>
              ) : logs.map((l, i) => (
                <div key={i} style={styles.logEntry(l.type)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                    <span style={{ fontWeight: '900', fontSize: '0.6rem', color: l.type === 'danger' ? '#f85149' : '#818cf8' }}>{l.type.toUpperCase()}</span>
                    <span style={{ opacity: 0.4, fontFamily: 'monospace' }}>{l.timestamp}</span>
                  </div>
                  {l.message}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.2); opacity: 0; } 100% { transform: scale(1); opacity: 0.5; } }
        
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #30363d; borderRadius: 2px; }
      `}</style>
    </div>
  );
};

export default App;